import { Component, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Radio Option
 */
export interface RadioOption {
  value: any;
  label: string;
  description?: string;
  disabled?: boolean;
}

/**
 * Radio Group Component
 * 
 * Accessible radio button group with label, description, and keyboard navigation.
 * 
 * @example
 * ```typescript
 * // In your component
 * selectedValue = signal('option1');
 * 
 * options: RadioOption[] = [
 *   { value: 'opt1', label: 'Opção 1', description: 'Descrição da opção 1' },
 *   { value: 'opt2', label: 'Opção 2' },
 *   { value: 'opt3', label: 'Opção 3', disabled: true }
 * ];
 * ```
 * 
 * ```html
 * <mg3p-radio-group
 *   label="Escolha uma opção"
 *   [options]="options"
 *   [value]="selectedValue()"
 *   (valueChange)="selectedValue.set($event)"
 *   orientation="vertical"
 * />
 * ```
 * 
 * Features:
 * - Vertical or horizontal orientation
 * - Option descriptions
 * - Disabled options
 * - Error state
 * - Keyboard navigation (Arrow keys, Space)
 * - WCAG 2.1 Level AA compliant
 */
@Component({
  selector: 'mg3p-radio-group',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div 
      class="radio-group"
      [class.radio-group--error]="error()"
      [class.radio-group--disabled]="disabled()"
    >
      <!-- Label -->
      @if (label()) {
        <div class="radio-group__label">
          {{ label() }}
          @if (required()) {
            <span class="radio-group__required" aria-label="obrigatório">*</span>
          }
        </div>
      }

      <!-- Help text -->
      @if (helpText()) {
        <div 
          [id]="groupId() + '-help'"
          class="radio-group__help-text"
        >
          {{ helpText() }}
        </div>
      }

      <!-- Radio options -->
      <div 
        class="radio-group__options"
        [class.radio-group__options--horizontal]="orientation() === 'horizontal'"
        role="radiogroup"
        [attr.aria-labelledby]="label() ? groupId() + '-label' : null"
        [attr.aria-describedby]="helpText() ? groupId() + '-help' : null"
        [attr.aria-required]="required()"
        [attr.aria-invalid]="error()"
      >
        @for (option of options(); track option.value) {
          <label
            class="radio-group__option"
            [class.radio-group__option--checked]="isSelected(option)"
            [class.radio-group__option--disabled]="option.disabled || disabled()"
          >
            <input
              type="radio"
              class="radio-group__input"
              [name]="groupId()"
              [value]="option.value"
              [checked]="isSelected(option)"
              [disabled]="option.disabled || disabled()"
              [required]="required()"
              (change)="onChange(option)"
              [attr.aria-label]="option.label"
            />
            
            <span class="radio-group__radio" aria-hidden="true">
              @if (isSelected(option)) {
                <span class="radio-group__dot"></span>
              }
            </span>

            <span class="radio-group__content">
              <span class="radio-group__option-label">{{ option.label }}</span>
              @if (option.description) {
                <span class="radio-group__option-description">{{ option.description }}</span>
              }
            </span>
          </label>
        }
      </div>

      <!-- Error message -->
      @if (errorMessage()) {
        <div class="radio-group__error" role="alert">
          <span aria-hidden="true">⚠️</span>
          {{ errorMessage() }}
        </div>
      }
    </div>
  `,
  styles: [`
    .radio-group {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-sm);
    }

    .radio-group__label {
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
      color: var(--color-text-primary);
    }

    .radio-group__required {
      color: var(--color-danger);
      margin-left: var(--spacing-xs);
    }

    .radio-group__help-text {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
    }

    .radio-group__options {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-sm);
    }

    .radio-group__options--horizontal {
      flex-direction: row;
      flex-wrap: wrap;
      gap: var(--spacing-md);
    }

    .radio-group__option {
      display: inline-flex;
      align-items: flex-start;
      gap: var(--spacing-sm);
      padding: var(--spacing-sm);
      border-radius: var(--radius-md);
      cursor: pointer;
      user-select: none;
      transition: background var(--transition-fast);
    }

    .radio-group__option:hover:not(.radio-group__option--disabled) {
      background: var(--color-surface-hover);
    }

    .radio-group__option--checked {
      background: var(--color-primary-light);
    }

    .radio-group__option--disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .radio-group__input {
      position: absolute;
      opacity: 0;
      width: 0;
      height: 0;
    }

    .radio-group__radio {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      flex-shrink: 0;
      background: var(--color-surface);
      border: 2px solid var(--color-border);
      border-radius: var(--radius-full);
      transition: all var(--transition-fast);
      margin-top: 2px;
    }

    .radio-group__option:hover:not(.radio-group__option--disabled) .radio-group__radio {
      border-color: var(--color-primary);
    }

    .radio-group__input:focus-visible + .radio-group__radio {
      outline: 2px solid var(--color-primary);
      outline-offset: 2px;
    }

    .radio-group__option--checked .radio-group__radio {
      border-color: var(--color-primary);
    }

    .radio-group__dot {
      width: 10px;
      height: 10px;
      background: var(--color-primary);
      border-radius: var(--radius-full);
      animation: scaleIn var(--transition-fast);
    }

    .radio-group__content {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
    }

    .radio-group__option-label {
      font-size: var(--font-size-md);
      font-weight: var(--font-weight-medium);
      color: var(--color-text-primary);
    }

    .radio-group__option-description {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
      line-height: 1.4;
    }

    .radio-group__error {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);
      font-size: var(--font-size-sm);
      color: var(--color-danger);
    }

    /* Error state */
    .radio-group--error .radio-group__radio {
      border-color: var(--color-danger);
    }

    .radio-group--error .radio-group__option--checked .radio-group__radio {
      border-color: var(--color-danger);
    }

    .radio-group--error .radio-group__dot {
      background: var(--color-danger);
    }

    /* Animations */
    @keyframes scaleIn {
      from {
        transform: scale(0);
      }
      to {
        transform: scale(1);
      }
    }

    /* Reduced motion */
    @media (prefers-reduced-motion: reduce) {
      .radio-group__option,
      .radio-group__radio,
      .radio-group__dot {
        animation: none;
        transition: none;
      }
    }
  `]
})
export class RadioGroupComponent {
  // Inputs
  label = input<string>('');
  helpText = input<string>('');
  options = input.required<RadioOption[]>();
  value = input<any>(null);
  orientation = input<'vertical' | 'horizontal'>('vertical');
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  error = input<boolean>(false);
  errorMessage = input<string>('');

  // Outputs
  valueChange = output<any>();

  // State
  groupId = signal<string>(`radio-group-${Math.random().toString(36).substr(2, 9)}`);

  onChange(option: RadioOption): void {
    if (option.disabled || this.disabled()) return;
    this.valueChange.emit(option.value);
  }

  isSelected(option: RadioOption): boolean {
    return option.value === this.value();
  }
}

// Made with Bob
